import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Mail, Send, Upload, Loader2, Trash2, X, ShieldCheck, AlertTriangle,
  FileText, Eye, EyeOff, History, CheckCircle2, Gauge, Info, Stethoscope, ExternalLink, KeyRound,
} from 'lucide-react';

/**
 * Admin · Mailing — Envoi d'emails depuis le back-office avec
 * l'adresse expéditrice `contact@code2roues.fr`.
 *
 * Fonctionnalités :
 *  • Saisie libre de destinataires (textarea) + import CSV/TXT
 *  • Validation regex côté client + dédup automatique
 *  • Aperçu HTML en temps réel
 *  • Mode "Test à moi-même" avant envoi de masse
 *  • Historique des 50 dernières campagnes
 *  • Lien de désinscription automatique (RGPD)
 */
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function parseEmails(rawText) {
  const valid = new Set();
  const invalid = [];
  (rawText || '').split(/[\s,;]+/).forEach((tok) => {
    const e = (tok || '').trim().toLowerCase();
    if (!e) return;
    if (EMAIL_REGEX.test(e)) valid.add(e);
    else invalid.push(tok.trim());
  });
  return { valid: Array.from(valid), invalid };
}

export default function AdminMailing() {
  const [recipientsText, setRecipientsText] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState(
    '<p>Bonjour,</p>\n\n<p>Votre message ici.</p>\n\n<p>L\'équipe CODE 2 ROUES</p>'
  );
  const [includeUnsubscribe, setIncludeUnsubscribe] = useState(true);
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const fileInputRef = useRef(null);

  const { valid: validEmails, invalid: invalidEmails } = useMemo(
    () => parseEmails(recipientsText),
    [recipientsText]
  );

  const loadCampaigns = async () => {
    try {
      const list = await api.adminMailingCampaigns();
      setCampaigns(Array.isArray(list) ? list : []);
    } catch (e) {
      // silent
    }
  };

  const loadQuota = async () => {
    try {
      const q = await api.adminMailingQuota();
      setQuotaInfo(q);
    } catch (e) {
      // silent
    }
  };

  const loadDiagnostic = async () => {
    setDiagnosticLoading(true);
    try {
      const d = await api.adminMailingDiagnostic();
      setDiagnostic(d);
    } catch (e) {
      toast.error('Diagnostic Resend : ' + e.message);
    } finally {
      setDiagnosticLoading(false);
    }
  };
  useEffect(() => { loadCampaigns(); loadQuota(); loadDiagnostic(); }, []);

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Détecte CSV (avec colonnes) ou liste brute (1 email par ligne / séparé)
      let extracted = '';
      if (/[,;]/.test(text)) {
        // Pour un CSV, on prend la 1ère colonne qui ressemble à un email
        const lines = text.split(/\r?\n/);
        const emails = [];
        lines.forEach((ln) => {
          if (!ln) return;
          const cells = ln.split(/[,;]/);
          cells.forEach((c) => {
            const v = c.replace(/['"]/g, '').trim();
            if (EMAIL_REGEX.test(v)) emails.push(v);
          });
        });
        extracted = emails.join('\n');
      } else {
        extracted = text;
      }
      const before = recipientsText.trim();
      const combined = (before ? before + '\n' : '') + extracted;
      setRecipientsText(combined);
      toast.success(`${extracted.split(/\s+/).filter(Boolean).length} entrées importées depuis ${file.name}`);
    } catch (e) {
      toast.error("Impossible de lire le fichier : " + e.message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearAll = () => {
    if (!window.confirm('Vider la liste de destinataires ?')) return;
    setRecipientsText('');
  };

  const sendMailing = async (testOnly) => {
    if (!subject.trim()) {
      toast.error('L\'objet est requis');
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error('Le message est requis');
      return;
    }
    if (!testOnly && validEmails.length === 0) {
      toast.error('Aucun destinataire valide');
      return;
    }

    // Garde-fou pré-envoi : vérifie le quota Resend si connu
    const needed = testOnly ? 1 : validEmails.length;
    if (quotaInfo) {
      const remDay = quotaInfo.remaining_today;
      const remMonth = quotaInfo.remaining_this_month;
      if (typeof remDay === 'number' && remDay <= 0) {
        toast.error(`Quota Resend journalier atteint (${quotaInfo.daily_quota}/jour). Réessayez demain ou upgradez votre plan Resend.`);
        return;
      }
      if (typeof remMonth === 'number' && remMonth <= 0) {
        toast.error(`Quota Resend mensuel atteint (${quotaInfo.monthly_quota}/mois). Upgradez votre plan Resend pour continuer.`);
        return;
      }
      if (typeof remDay === 'number' && needed > remDay) {
        const ok = window.confirm(
          `⚠️ Vous voulez envoyer ${needed} email(s) mais il ne reste que ${remDay} envoi(s) aujourd'hui sur votre quota Resend (${quotaInfo.daily_quota}/jour).\n\nLes premiers seront envoyés, les suivants échoueront.\n\nContinuer quand même ?`
        );
        if (!ok) return;
      }
    }

    setSending(true);
    try {
      const res = await api.adminMailingSend({
        recipients: testOnly ? [] : validEmails,
        subject: subject.trim(),
        body_html: bodyHtml,
        cta_label: ctaLabel.trim() || null,
        cta_url: ctaUrl.trim() || null,
        include_unsubscribe: includeUnsubscribe,
        test_only: !!testOnly,
      });
      setLastResult(res);
      if (testOnly) {
        toast.success('Email de test envoyé à votre adresse admin');
      } else {
        const msg = `${res.sent_count}/${res.recipients_count} email${res.sent_count > 1 ? 's' : ''} envoyé${res.sent_count > 1 ? 's' : ''}`;
        if (res.failed_count > 0) {
          // Affiche le 1er détail d'erreur directement dans le toast pour visibilité immédiate
          const firstErr = res.sample_failed?.[0]?.error || 'voir détails ci-dessous';
          toast.warning(`${msg} — ${res.failed_count} échec(s). Cause : ${firstErr}`, { duration: 10000 });
        } else {
          toast.success(msg);
        }
      }
      await loadCampaigns();
      await loadQuota();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-mailing">
      <div className="flex items-center gap-3">
        <Mail className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-black">Mailing</h1>
          <p className="text-sm text-muted-foreground">
            Envoi d'emails depuis <code className="text-foreground bg-muted px-1.5 py-0.5 rounded">contact@code2roues.fr</code>
          </p>
        </div>
      </div>

      {/* Bandeau quota Resend — visibilité permanente */}
      <QuotaBanner quota={quotaInfo} onRefresh={loadQuota} />

      {/* Diagnostic Resend — utile pour repérer un mismatch de workspace/clé */}
      <DiagnosticBanner
        diagnostic={diagnostic}
        loading={diagnosticLoading}
        onRefresh={loadDiagnostic}
      />

      {/* Permet à l'admin de modifier la clé Resend depuis le back-office,
          sans redéploiement. Utile en cas de mismatch env vs workspace. */}
      <ResendApiKeyEditor diagnostic={diagnostic} onSaved={loadDiagnostic} />

      {/* Permet à l'admin de modifier l'email expéditeur depuis le back-office,
          sans redéploiement. Crucial quand SENDER_EMAIL est absent en prod. */}
      <SenderEmailEditor diagnostic={diagnostic} onSaved={loadDiagnostic} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne 1 : destinataires + contenu */}
        <div className="lg:col-span-2 space-y-6">
          {/* Section Destinataires */}
          <Card data-testid="mailing-recipients-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Destinataires
                <Badge variant="secondary" data-testid="mailing-valid-count">
                  {validEmails.length} valide{validEmails.length > 1 ? 's' : ''}
                </Badge>
                {invalidEmails.length > 0 && (
                  <Badge variant="destructive" data-testid="mailing-invalid-count">
                    {invalidEmails.length} invalide{invalidEmails.length > 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileImport}
                  className="hidden"
                  data-testid="mailing-file-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="mailing-import-btn"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> Importer (.csv / .txt)
                </Button>
                {recipientsText && (
                  <Button variant="ghost" size="sm" onClick={clearAll} data-testid="mailing-clear-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Vider
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder={"Collez ou tapez les adresses email ici\nUn par ligne, ou séparées par virgule / point-virgule\n\nexemple1@email.fr\nexemple2@email.fr"}
                rows={6}
                className="font-mono text-sm"
                data-testid="mailing-recipients-textarea"
              />
              {invalidEmails.length > 0 && (
                <div className="mt-3 p-3 rounded-md border border-destructive/30 bg-destructive/5 text-xs text-destructive flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>{invalidEmails.length} entrée(s) invalide(s)</strong>{' '}
                    (ignorée(s) à l'envoi) :{' '}
                    <span className="font-mono">
                      {invalidEmails.slice(0, 5).join(', ')}
                      {invalidEmails.length > 5 && ` … +${invalidEmails.length - 5}`}
                    </span>
                  </div>
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Quota : 500 destinataires max par envoi. Les doublons sont automatiquement dédupliqués.
              </p>
            </CardContent>
          </Card>

          {/* Section Contenu */}
          <Card data-testid="mailing-content-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contenu de l'email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="mailing-subject">Objet *</Label>
                <Input
                  id="mailing-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ex : Nouveauté CODE 2 ROUES — découvrez nos pages piliers"
                  className="mt-1"
                  data-testid="mailing-subject-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Sera préfixé automatiquement par <code>[CODE 2 ROUES]</code> si absent.
                </p>
              </div>
              <div>
                <Label htmlFor="mailing-body">Message (HTML supporté) *</Label>
                <Textarea
                  id="mailing-body"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={10}
                  className="mt-1 font-mono text-sm"
                  data-testid="mailing-body-textarea"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Le contenu est inséré dans le template officiel CODE 2 ROUES (header logo, footer signature).
                  Balises HTML autorisées : <code>&lt;p&gt; &lt;b&gt; &lt;i&gt; &lt;a&gt; &lt;br&gt; &lt;ul&gt; &lt;li&gt; &lt;h2&gt; &lt;img&gt;</code> etc.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="mailing-cta-label">Bouton d'action (optionnel)</Label>
                  <Input
                    id="mailing-cta-label"
                    value={ctaLabel}
                    onChange={(e) => setCtaLabel(e.target.value)}
                    placeholder="Ex : Voir la nouveauté"
                    className="mt-1"
                    data-testid="mailing-cta-label-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mailing-cta-url">URL du bouton</Label>
                  <Input
                    id="mailing-cta-url"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    placeholder="https://code2roues.fr/..."
                    className="mt-1"
                    data-testid="mailing-cta-url-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  id="mailing-unsubscribe"
                  checked={includeUnsubscribe}
                  onCheckedChange={setIncludeUnsubscribe}
                  data-testid="mailing-unsubscribe-switch"
                />
                <Label htmlFor="mailing-unsubscribe" className="cursor-pointer">
                  Inclure un lien de désinscription
                  <span className="block text-xs text-muted-foreground font-normal">
                    Recommandé pour les newsletters & communications marketing (RGPD).
                  </span>
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne 2 : actions + preview */}
        <div className="space-y-6">
          {/* Aperçu */}
          <Card data-testid="mailing-preview-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Aperçu rapide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-border bg-white text-black p-4 max-h-80 overflow-y-auto text-sm">
                <div className="border-b pb-2 mb-3 text-xs text-gray-500">
                  <div><strong>De :</strong> CODE 2 ROUES &lt;contact@code2roues.fr&gt;</div>
                  <div><strong>Objet :</strong> {subject ? (subject.toLowerCase().startsWith('[code 2 roues]') ? subject : `[CODE 2 ROUES] ${subject}`) : <em className="text-gray-400">(à compléter)</em>}</div>
                </div>
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: bodyHtml || '<em>(corps vide)</em>' }}
                />
                {ctaLabel && ctaUrl && (
                  <div className="mt-4 text-center">
                    <a
                      href={ctaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block px-4 py-2 bg-[#0b1b3a] text-white rounded-md text-sm font-semibold"
                    >
                      {ctaLabel}
                    </a>
                  </div>
                )}
                {includeUnsubscribe && (
                  <div className="mt-6 pt-3 border-t text-[10px] text-gray-400">
                    Vous recevez cet email car vous êtes inscrit(e) à la communication de CODE 2 ROUES.
                    <a href="#" className="underline ml-1">Se désinscrire</a>.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card data-testid="mailing-actions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Envoyer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => sendMailing(true)}
                disabled={sending || !subject.trim() || !bodyHtml.trim()}
                data-testid="mailing-send-test-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Test à moi-même
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="w-full"
                    disabled={sending || !subject.trim() || !bodyHtml.trim() || validEmails.length === 0}
                    data-testid="mailing-send-all-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Envoyer à {validEmails.length} destinataire{validEmails.length > 1 ? 's' : ''}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="mailing-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer l'envoi</AlertDialogTitle>
                    <AlertDialogDescription>
                      Vous êtes sur le point d'envoyer un email à <strong>{validEmails.length} destinataire{validEmails.length > 1 ? 's' : ''}</strong>{' '}
                      depuis <code>contact@code2roues.fr</code>.
                      <br /><br />
                      <strong>Objet :</strong> {subject}
                      <br />
                      <strong>Désinscription RGPD :</strong> {includeUnsubscribe ? '✅ Incluse' : '⚠️ Non incluse'}
                      <br /><br />
                      Cette action est <strong>irréversible</strong>.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => sendMailing(false)}
                      data-testid="mailing-confirm-action"
                    >
                      Confirmer l'envoi
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <p className="text-xs text-muted-foreground pt-2">
                ⚠️ Conformité RGPD : n'envoyez qu'aux personnes ayant consenti ou à votre clientèle légitime.
                Tous les envois sont tracés dans l'onglet « Logs emails ».
              </p>

              {/* Détail du dernier envoi : très utile pour diagnostiquer les échecs */}
              {lastResult && (
                <div
                  className={`mt-3 p-3 rounded-md text-xs border ${
                    lastResult.failed_count > 0
                      ? 'border-destructive/40 bg-destructive/5 text-destructive'
                      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  }`}
                  data-testid="mailing-last-result"
                >
                  <div className="font-semibold mb-1">
                    {lastResult.failed_count > 0 ? '❌ ' : '✅ '}
                    {lastResult.sent_count}/{lastResult.recipients_count} email
                    {lastResult.sent_count > 1 ? 's' : ''} envoyé
                    {lastResult.sent_count > 1 ? 's' : ''}
                    {lastResult.failed_count > 0 && ` (${lastResult.failed_count} échec${lastResult.failed_count > 1 ? 's' : ''})`}
                  </div>
                  {lastResult.sample_failed?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-[11px] uppercase tracking-wider opacity-80">Détails des échecs :</div>
                      {lastResult.sample_failed.map((f, i) => (
                        <div key={i} className="font-mono text-[11px] break-all">
                          • <strong>{f.email}</strong> : {f.error}
                        </div>
                      ))}
                    </div>
                  )}
                  {lastResult.sample_invalid?.length > 0 && (
                    <div className="mt-2 text-[11px] opacity-80">
                      Emails invalides ignorés : {lastResult.sample_invalid.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Historique campagnes */}
      <Card data-testid="mailing-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historique des envois ({campaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aucune campagne envoyée pour le moment.
            </p>
          ) : (
            <div className="space-y-2" data-testid="mailing-history-list">
              {campaigns.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-3 rounded-md border border-border bg-card hover:bg-accent/40 transition-colors"
                  data-testid={`mailing-history-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {c.test_only ? (
                        <Badge variant="outline" className="text-xs">TEST</Badge>
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      )}
                      <span className="font-semibold text-sm truncate">{c.subject}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(c.created_at).toLocaleString('fr-FR')} · par {c.sent_by}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                    <Badge variant="secondary">
                      {c.sent_count}/{c.recipients_count} envoyés
                    </Badge>
                    {c.failed_count > 0 && (
                      <Badge variant="destructive">
                        {c.failed_count} échecs
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * Bandeau de quota Resend — affiche en permanence le plan en cours
 * et la consommation du jour/du mois pour éviter les "1 échec" mystérieux.
 *
 * Note : si daily_quota / monthly_quota sont null (jamais reçus de Resend
 * depuis le démarrage du backend), on affiche un état neutre avec un
 * bouton "Vérifier" qui force un envoi de test.
 */
function QuotaBanner({ quota, onRefresh }) {
  if (!quota) {
    return (
      <div
        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30 text-xs text-muted-foreground"
        data-testid="mailing-quota-banner-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Chargement du quota Resend…
      </div>
    );
  }

  const daily = quota.daily_quota;
  const monthly = quota.monthly_quota;
  const sentToday = quota.sent_today ?? 0;
  const sentMonth = quota.sent_this_month ?? 0;
  const remDay = quota.remaining_today;
  const remMonth = quota.remaining_this_month;

  // Si aucune info Resend captée encore, état info neutre
  if (daily == null && monthly == null) {
    return (
      <div
        className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 text-xs"
        data-testid="mailing-quota-banner-unknown"
      >
        <Info className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-foreground">Quota Resend : non détecté</div>
          <div className="text-muted-foreground mt-0.5">
            Le quota sera lu après votre premier envoi. Envois aujourd'hui : <strong>{sentToday}</strong> · ce mois : <strong>{sentMonth}</strong>.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="mailing-quota-refresh-btn">
          Rafraîchir
        </Button>
      </div>
    );
  }

  // Calculs visuels
  const dailyPct = (typeof daily === 'number' && daily > 0)
    ? Math.min(100, Math.round((sentToday / daily) * 100))
    : 0;
  const monthlyPct = (typeof monthly === 'number' && monthly > 0)
    ? Math.min(100, Math.round((sentMonth / monthly) * 100))
    : 0;

  const dailyExceeded = typeof remDay === 'number' && remDay <= 0;
  const monthlyExceeded = typeof remMonth === 'number' && remMonth <= 0;
  const dailyWarning = typeof remDay === 'number' && remDay > 0 && remDay <= Math.max(1, Math.floor((daily || 0) * 0.25));
  const monthlyWarning = typeof remMonth === 'number' && remMonth > 0 && remMonth <= Math.max(1, Math.floor((monthly || 0) * 0.25));

  const isCritical = dailyExceeded || monthlyExceeded;
  const isWarning = !isCritical && (dailyWarning || monthlyWarning);

  // Détection plan free (signal fort si daily ≤ 10)
  const looksLikeFreePlan = typeof daily === 'number' && daily <= 10;

  const containerCls = isCritical
    ? 'border-destructive/50 bg-destructive/5'
    : isWarning
      ? 'border-amber-500/50 bg-amber-500/5'
      : 'border-border bg-muted/30';

  return (
    <div
      className={`rounded-lg border ${containerCls} p-4`}
      data-testid="mailing-quota-banner"
    >
      <div className="flex items-start gap-3">
        <Gauge className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isCritical ? 'text-destructive' : isWarning ? 'text-amber-600' : 'text-primary'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-bold text-sm">Quota Resend</span>
            {looksLikeFreePlan && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider" data-testid="mailing-quota-free-badge">
                Plan gratuit
              </Badge>
            )}
            {isCritical && (
              <Badge variant="destructive" className="text-[10px]" data-testid="mailing-quota-exceeded-badge">
                Quota atteint
              </Badge>
            )}
            {isWarning && (
              <Badge className="text-[10px] bg-amber-500 hover:bg-amber-600" data-testid="mailing-quota-warning-badge">
                Quota presque atteint
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {/* Quota journalier */}
            {typeof daily === 'number' && (
              <div data-testid="mailing-quota-daily">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">Aujourd'hui</span>
                  <span className={`font-mono ${dailyExceeded ? 'text-destructive font-bold' : dailyWarning ? 'text-amber-600 font-bold' : 'text-muted-foreground'}`}>
                    {sentToday} / {daily} {typeof remDay === 'number' && `(${remDay} restant${remDay > 1 ? 's' : ''})`}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all ${dailyExceeded ? 'bg-destructive' : dailyWarning ? 'bg-amber-500' : 'bg-primary'}`}
                    style={{ width: `${dailyPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Quota mensuel */}
            {typeof monthly === 'number' && (
              <div data-testid="mailing-quota-monthly">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">Ce mois-ci</span>
                  <span className={`font-mono ${monthlyExceeded ? 'text-destructive font-bold' : monthlyWarning ? 'text-amber-600 font-bold' : 'text-muted-foreground'}`}>
                    {sentMonth} / {monthly} {typeof remMonth === 'number' && `(${remMonth} restant${remMonth > 1 ? 's' : ''})`}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all ${monthlyExceeded ? 'bg-destructive' : monthlyWarning ? 'bg-amber-500' : 'bg-primary'}`}
                    style={{ width: `${monthlyPct}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {(isCritical || isWarning || looksLikeFreePlan) && (
            <div className={`mt-3 text-[11px] leading-relaxed ${isCritical ? 'text-destructive' : 'text-muted-foreground'}`}>
              {isCritical && (
                <p className="font-semibold mb-1">
                  ⚠️ Quota Resend épuisé : tout nouvel envoi sera rejeté avec une erreur 429.
                </p>
              )}
              {looksLikeFreePlan && (
                <p>
                  💡 Votre compte Resend est sur le <strong>plan gratuit</strong> ({daily}/jour, {monthly}/mois).
                  Pour des newsletters de masse,{' '}
                  <a
                    href="https://resend.com/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold hover:no-underline"
                    data-testid="mailing-quota-upgrade-link"
                  >
                    upgradez le plan Resend
                  </a>{' '}
                  (plan Pro à 20 $/mois = 50 000 envois mensuels).
                </p>
              )}
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          className="flex-shrink-0"
          data-testid="mailing-quota-refresh-btn"
          title="Rafraîchir les compteurs"
        >
          <Loader2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Diagnostic Resend — carte d'informations pour identifier rapidement
 * un éventuel mismatch entre la clé API utilisée et le workspace Resend
 * contenant les domaines vérifiés.
 *
 * Utile en particulier quand l'envoi échoue avec "You can only send testing
 * emails to your own email address" : signe quasi-certain que la clé API
 * pointe sur le mauvais workspace OU que le domaine n'a pas été vérifié
 * dans ce workspace.
 *
 * Affiche :
 *  - Préfixe + suffixe de la clé (le reste est masqué pour la sécurité)
 *  - Email expéditeur configuré
 *  - Statut vérifié/non vérifié du domaine du sender
 *  - Liste de tous les domaines visibles par cette clé + leur statut
 *  - Environnement (preview/production) si détectable
 */
function DiagnosticBanner({ diagnostic, loading, onRefresh }) {
  if (loading && !diagnostic) {
    return (
      <div
        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30 text-xs text-muted-foreground"
        data-testid="mailing-diagnostic-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Chargement du diagnostic Resend…
      </div>
    );
  }
  if (!diagnostic) return null;

  const {
    configured, api_key_prefix, api_key_suffix, api_key_length,
    sender_email, sender_domain, sender_domain_verified,
    domains, domains_error, environment_hint,
  } = diagnostic;

  const isOk = configured && sender_domain_verified === true;
  const isProblematic = configured && sender_domain_verified === false;
  const isUnconfigured = !configured;

  const containerCls = isProblematic
    ? 'border-destructive/50 bg-destructive/5'
    : isOk
      ? 'border-emerald-500/40 bg-emerald-500/5'
      : 'border-border bg-muted/30';

  return (
    <div
      className={`rounded-lg border ${containerCls} p-4`}
      data-testid="mailing-diagnostic-banner"
    >
      <div className="flex items-start gap-3">
        <Stethoscope className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isProblematic ? 'text-destructive' : isOk ? 'text-emerald-600' : 'text-primary'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-bold text-sm">Diagnostic Resend</span>
            {environment_hint === 'preview' && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                Environnement preview
              </Badge>
            )}
            {environment_hint === 'production' && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                Environnement production
              </Badge>
            )}
            {isOk && (
              <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-700" data-testid="diagnostic-ok-badge">
                ✓ Configuration OK
              </Badge>
            )}
            {isProblematic && (
              <Badge variant="destructive" className="text-[10px]" data-testid="diagnostic-error-badge">
                ⚠ Domaine non vérifié
              </Badge>
            )}
            {isUnconfigured && (
              <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">
                Clé API absente
              </Badge>
            )}
          </div>

          {/* Tableau résumé */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <DiagRow label="Clé API Resend">
              {api_key_prefix ? (
                <code className="font-mono text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">
                  {api_key_prefix}…{api_key_suffix} <span className="opacity-60">({api_key_length} car.)</span>
                </code>
              ) : (
                <span className="text-destructive font-semibold">Aucune clé configurée</span>
              )}
            </DiagRow>

            <DiagRow label="Email expéditeur">
              <code className="font-mono text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">
                {sender_email || '—'}
              </code>
            </DiagRow>

            <DiagRow label="Domaine du sender">
              <code className="font-mono text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">
                {sender_domain || '—'}
              </code>
              {sender_domain_verified === true && (
                <span className="ml-2 text-emerald-600 font-bold text-[11px]">✓ vérifié</span>
              )}
              {sender_domain_verified === false && (
                <span className="ml-2 text-destructive font-bold text-[11px]">✗ NON vérifié</span>
              )}
            </DiagRow>

            <DiagRow label="Domaines vus par cette clé">
              {Array.isArray(domains) && domains.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {domains.map((d) => (
                    <span
                      key={d.id || d.name}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${
                        d.status === 'verified' && d.sending_enabled
                          ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
                          : 'bg-amber-500/10 text-amber-600 border border-amber-500/30'
                      }`}
                    >
                      {d.name} · {d.status}{d.region ? ` · ${d.region}` : ''}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-destructive">
                  Aucun domaine — la clé pointe sur un workspace vide ou différent
                </span>
              )}
            </DiagRow>
          </div>

          {domains_error && (
            <div className="mt-3 p-2 rounded border border-destructive/40 bg-destructive/5 text-[11px] text-destructive font-mono break-all">
              Erreur API Resend : {domains_error}
            </div>
          )}

          {/* Bloc d'aide contextuel quand le domaine du sender n'est pas vérifié */}
          {isProblematic && (
            <div className="mt-3 p-3 rounded-md border border-destructive/40 bg-destructive/10 text-xs leading-relaxed text-destructive space-y-2">
              <p className="font-bold">
                Pourquoi vos envois échouent avec "testing only" :
              </p>
              <p>
                La clé API Resend ci-dessus pointe sur un <strong>workspace</strong> où le domaine{' '}
                <code className="bg-background/40 px-1 rounded">{sender_domain}</code> n'est pas vérifié.
                Resend bloque alors tout envoi sauf vers l'adresse propriétaire du compte.
              </p>
              <p className="font-semibold">2 solutions :</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>
                  <strong>Soit</strong> remplacer la clé `RESEND_API_KEY` par celle du workspace
                  qui a déjà vérifié ce domaine{' '}
                  <a
                    href="https://resend.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-bold inline-flex items-center gap-1"
                  >
                    (resend.com/api-keys <ExternalLink className="w-3 h-3" />)
                  </a>
                </li>
                <li>
                  <strong>Soit</strong> ajouter et vérifier{' '}
                  <code className="bg-background/40 px-1 rounded">{sender_domain}</code> dans le
                  workspace actuel{' '}
                  <a
                    href="https://resend.com/domains"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-bold inline-flex items-center gap-1"
                  >
                    (resend.com/domains <ExternalLink className="w-3 h-3" />)
                  </a>
                </li>
              </ol>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="flex-shrink-0"
          data-testid="mailing-diagnostic-refresh-btn"
          title="Re-tester la configuration Resend"
        >
          {loading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Loader2 className="w-3.5 h-3.5" />
          }
        </Button>
      </div>
    </div>
  );
}

function DiagRow({ label, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{label}</span>
      <div className="flex items-center flex-wrap">{children}</div>
    </div>
  );
}


/**
 * Éditeur de la clé API Resend stockée en BDD (admin-only).
 *
 * Permet à l'admin de :
 *  - Voir la source actuelle de la clé (env Emergent vs override BDD)
 *  - Saisir une nouvelle clé et la TESTER (lister les domaines + envoyer un mail
 *    de test) AVANT de la sauvegarder en BDD
 *  - Sauvegarder définitivement la nouvelle clé (prend effet immédiatement)
 *  - Effacer l'override BDD pour redonner la main à la variable d'env
 *
 * Use-case principal : résoudre le bug "1 échec" quand le domaine est verified
 * en preview mais pas en prod (deux clés Resend différentes). Plus besoin de
 * redéployer ni de toucher aux env Emergent.
 */
function ResendApiKeyEditor({ diagnostic, onSaved }) {
  const [newKey, setNewKey] = React.useState('');
  const [showKey, setShowKey] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  if (!diagnostic) return null;

  const currentSource = diagnostic.api_key_source;
  const sourceLabel = currentSource === 'db' ? 'Stockée en BDD (override admin)'
    : currentSource === 'env' ? 'Variable d\'environnement Emergent'
      : 'Aucune clé active';
  const sourceColor = currentSource === 'db' ? 'text-amber-600'
    : currentSource === 'env' ? 'text-emerald-600' : 'text-destructive';

  const handleTest = async () => {
    if (!newKey || !newKey.startsWith('re_')) {
      toast.error('La clé doit commencer par "re_"');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.adminMailingTestApiKey(newKey);
      setTestResult(r);
      if (r.valid) {
        toast.success(`Clé valide ! Email test envoyé à ${r.test_send_to}`);
      } else {
        toast.error(r.test_send_error || r.domains_error || 'Test échoué');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!newKey) {
      toast.error('Saisissez une clé');
      return;
    }
    if (!window.confirm(
      `Confirmer ?\n\nLa clé sera enregistrée en base de données et utilisée IMMÉDIATEMENT pour tous les envois de mail du site. Elle remplace la clé d'environnement (si présente).`
    )) return;
    setSaving(true);
    try {
      const r = await api.adminMailingSetApiKey(newKey);
      toast.success(`Clé Resend mise à jour (source: ${r.active_source})`);
      setNewKey(''); setTestResult(null);
      await onSaved?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(
      `Supprimer l'override BDD ?\n\nLa clé d'environnement Emergent (RESEND_API_KEY) reprendra la main. Si elle n'est pas définie en prod, les envois échoueront jusqu'à la définition d'une nouvelle clé.`
    )) return;
    setClearing(true);
    try {
      await api.adminMailingSetApiKey('');
      toast.success('Override BDD supprimé. La clé d\'env est de nouveau active.');
      await onSaved?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card data-testid="mailing-resend-key-editor">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="w-4 h-4" />
          Clé API Resend (override admin)
        </CardTitle>
        <CardDescription>
          Modifier la clé Resend depuis le back-office, sans redéploiement.
          Utile en cas de mismatch entre l'env Emergent et le workspace Resend qui contient le domaine vérifié.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs flex flex-wrap items-center gap-3">
          <span className="font-semibold text-muted-foreground">Source actuelle :</span>
          <span className={`font-bold ${sourceColor}`} data-testid="resend-key-source">
            {sourceLabel}
          </span>
          {currentSource === 'db' && diagnostic.api_key_db_updated_at && (
            <span className="text-[10px] text-muted-foreground">
              · MAJ le {new Date(diagnostic.api_key_db_updated_at).toLocaleString('fr-FR')}
              {diagnostic.api_key_db_updated_by && ` par ${diagnostic.api_key_db_updated_by}`}
            </span>
          )}
          {currentSource === 'db' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={clearing}
              data-testid="resend-key-clear-btn"
              className="ml-auto h-7 text-[11px]"
            >
              {clearing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Supprimer l'override
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="resend-new-key" className="text-xs font-semibold">
            Nouvelle clé Resend (commence par <code className="bg-muted px-1 rounded">re_</code>)
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="resend-new-key"
                type={showKey ? 'text' : 'password'}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxx"
                className="pr-10 font-mono text-xs"
                autoComplete="off"
                data-testid="resend-new-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label={showKey ? 'Masquer la clé' : 'Afficher la clé'}
              >
                {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            La clé est stockée chiffrée en base. Récupérez votre clé sur{' '}
            <a
              href="https://resend.com/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              resend.com/api-keys
            </a>.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={!newKey || testing || saving}
            data-testid="resend-key-test-btn"
            className="sm:min-w-[180px]"
          >
            {testing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</>
              : <><Stethoscope className="w-4 h-4 mr-2" />Tester avant sauvegarde</>}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!newKey || saving}
            data-testid="resend-key-save-btn"
            className="sm:min-w-[200px]"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement…</>
              : <><CheckCircle2 className="w-4 h-4 mr-2" />Enregistrer la clé</>}
          </Button>
        </div>

        {/* Résultat du test affiché juste après */}
        {testResult && (
          <div
            className={`rounded-md border p-3 text-xs space-y-2 ${
              testResult.valid
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-destructive/40 bg-destructive/5'
            }`}
            data-testid="resend-key-test-result"
          >
            <p className={`font-bold ${testResult.valid ? 'text-emerald-700' : 'text-destructive'}`}>
              {testResult.valid ? '✓ Clé valide' : '✗ Clé invalide ou échec'} — {testResult.key_prefix}…{testResult.key_suffix}
            </p>
            {Array.isArray(testResult.domains) && testResult.domains.length > 0 && (
              <div>
                <span className="font-semibold">Domaines visibles : </span>
                {testResult.domains.map((d, i) => (
                  <span key={i} className={`inline-block mr-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                    d.status === 'verified' && d.sending_enabled
                      ? 'bg-emerald-500/10 text-emerald-700'
                      : 'bg-amber-500/10 text-amber-700'
                  }`}>
                    {d.name}·{d.status}
                  </span>
                ))}
              </div>
            )}
            {testResult.test_send_ok && (
              <p className="text-emerald-700">
                ✓ Email de test envoyé à <strong>{testResult.test_send_to}</strong>
                {testResult.test_send_id && ` (id: ${testResult.test_send_id.slice(0, 12)}…)`}
              </p>
            )}
            {testResult.test_send_error && (
              <p className="text-destructive font-mono break-all">
                Erreur : {testResult.test_send_error}
              </p>
            )}
            {testResult.domains_error && (
              <p className="text-destructive font-mono break-all">
                Erreur API : {testResult.domains_error}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ============================================================================
// SenderEmailEditor — Permet à l'admin de configurer l'adresse expéditeur
// (FROM email/name) directement depuis le back-office, sans toucher aux
// variables d'environnement. Priorité : BDD > env > fallback.
// ============================================================================
function SenderEmailEditor({ diagnostic, onSaved }) {
  const [sender, setSender] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [newEmail, setNewEmail] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminMailingGetSender();
      setSender(r);
      // Pré-remplit les champs avec la valeur active actuelle
      setNewEmail(r.active_email || '');
      setNewName(r.active_name || 'CODE 2 ROUES');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading || !sender) {
    return (
      <Card data-testid="mailing-sender-editor-loading">
        <CardContent className="py-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement de la configuration sender…
        </CardContent>
      </Card>
    );
  }

  const sourceLabel = sender.source === 'db' ? 'Stocké en BDD (override admin)'
    : sender.source === 'env' ? 'Variable d\'environnement Emergent'
      : 'Valeur de secours (fallback code)';
  const sourceColor = sender.source === 'db' ? 'text-amber-600'
    : sender.source === 'env' ? 'text-emerald-600' : 'text-destructive';

  // Validation simple côté client
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((newEmail || '').trim());
  const domain = (newEmail || '').includes('@') ? newEmail.split('@')[1].toLowerCase() : '';
  const senderDomainVerified = diagnostic && Array.isArray(diagnostic.domains)
    && diagnostic.domains.some(d =>
      (d.name || '').toLowerCase() === domain
      && d.status === 'verified'
      && d.sending_enabled
    );

  const handleSave = async () => {
    if (!emailValid) {
      toast.error('Email invalide');
      return;
    }
    if (!senderDomainVerified) {
      if (!window.confirm(
        `⚠ Le domaine "${domain}" n'est PAS vérifié dans le workspace Resend actuel.\n\nResend BLOQUERA tous les envois sauf vers l'adresse propriétaire du compte.\n\nVoulez-vous quand même enregistrer cet email expéditeur ?`
      )) return;
    } else {
      if (!window.confirm(
        `Confirmer ?\n\nL'email expéditeur sera "${newName} <${newEmail}>" pour TOUS les envois du site (transactionnels + mailing).`
      )) return;
    }
    setSaving(true);
    try {
      const r = await api.adminMailingSetSender(newEmail.trim(), (newName || '').trim() || null);
      toast.success(`Email expéditeur mis à jour : ${r.active_email}`);
      await load();
      await onSaved?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(
      `Supprimer l'override BDD ?\n\nLa variable d'environnement SENDER_EMAIL reprendra la main (ou le fallback "contact@code2roues.fr" si absente).`
    )) return;
    setClearing(true);
    try {
      await api.adminMailingSetSender('', null);
      toast.success('Override BDD supprimé.');
      await load();
      await onSaved?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card data-testid="mailing-sender-editor">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-4 h-4" />
          Email expéditeur (override admin)
        </CardTitle>
        <CardDescription>
          Configurer l'adresse et le nom expéditeur directement depuis le back-office,
          sans toucher aux variables d'environnement. Le domaine de l'email doit être
          vérifié dans votre workspace Resend pour que les envois fonctionnent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* État actuel */}
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold text-muted-foreground">Source actuelle :</span>
            <span className={`font-bold ${sourceColor}`} data-testid="sender-source">
              {sourceLabel}
            </span>
            {sender.source === 'db' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={clearing}
                data-testid="sender-clear-btn"
                className="ml-auto h-7 text-[11px]"
              >
                {clearing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Supprimer l'override
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
            <span>
              <span className="text-muted-foreground">Email actif : </span>
              <code className="bg-muted px-1.5 py-0.5 rounded font-mono" data-testid="sender-active-email">
                {sender.active_email}
              </code>
            </span>
            <span>
              <span className="text-muted-foreground">Nom affiché : </span>
              <code className="bg-muted px-1.5 py-0.5 rounded font-mono">{sender.active_name}</code>
            </span>
          </div>
        </div>

        {/* Formulaire */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="sender-new-email" className="text-xs font-semibold">
              Email expéditeur
            </Label>
            <Input
              id="sender-new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="contact@code2roues.fr"
              autoComplete="off"
              className="font-mono text-xs"
              data-testid="sender-email-input"
            />
            {newEmail && !emailValid && (
              <p className="text-[10px] text-destructive">Format d'email invalide</p>
            )}
            {newEmail && emailValid && (
              <p className={`text-[10px] ${senderDomainVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                {senderDomainVerified
                  ? `✓ Domaine "${domain}" vérifié dans le workspace Resend`
                  : `⚠ Domaine "${domain}" NON vérifié dans ce workspace — les envois échoueront`}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sender-new-name" className="text-xs font-semibold">
              Nom affiché (FROM)
            </Label>
            <Input
              id="sender-new-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="CODE 2 ROUES"
              autoComplete="off"
              className="text-xs"
              data-testid="sender-name-input"
            />
            <p className="text-[10px] text-muted-foreground">
              Affiché comme : <code className="bg-muted px-1 rounded">{(newName || 'CODE 2 ROUES')} &lt;{newEmail || 'email@…'}&gt;</code>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleSave}
            disabled={!emailValid || saving}
            data-testid="sender-save-btn"
            className="sm:min-w-[220px]"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement…</>
              : <><CheckCircle2 className="w-4 h-4 mr-2" />Enregistrer l'expéditeur</>}
          </Button>
          {diagnostic && Array.isArray(diagnostic.domains) && diagnostic.domains.length > 0 && (
            <div className="text-[11px] text-muted-foreground self-center">
              💡 Domaines vérifiés dispo :
              {diagnostic.domains
                .filter(d => d.status === 'verified' && d.sending_enabled)
                .map(d => (
                  <code key={d.id} className="ml-1 bg-emerald-500/10 text-emerald-700 px-1 rounded font-mono">
                    {d.name}
                  </code>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
